import React, { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ReportViewPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedItem = location.state?.item;
  const contentRef = useRef();

  const excludedFields = ['ROWID', 'CREATORID', 'MODIFIEDTIME', 'CREATEDTIME'];

  const formatLabel = (key) => {
    if (!key && key !== 0) return '';
    const labelMap = {
      formname: 'Form Name',
      reported_date: 'Reported Date',
      reported_by: 'Reported By',
      CREATEDTIME: 'Created Time',
      store_id: 'Store Id',
    };

    if (labelMap[key]) return labelMap[key];
    let s = String(key).replace(/_/g, ' ');
    s = s.replace(/([a-z])([A-Z])/g, '$1 $2');
    return s
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  const displayValue = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'string' && val.trim() === '') return '-';
    return String(val);
  };

  const handleDownloadPDF = async () => {
    const input = contentRef.current;

    try {
      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const marginMm = 10;
      const contentWidthMm = pdfWidth - marginMm * 2;
      const contentHeightMm = pdfHeight - marginMm * 2;
      const mmPerPixel = contentWidthMm / canvas.width;

      const pageHeightPx = Math.floor((contentHeightMm - 10) / mmPerPixel);
      let yOffset = 0;
      let pageIndex = 0;

      while (yOffset < canvas.height) {
        const remainingPx = canvas.height - yOffset;
        const sliceHeightPx = Math.min(pageHeightPx, remainingPx);

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const sliceCtx = sliceCanvas.getContext('2d');

        sliceCtx.drawImage(canvas, 0, yOffset, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
        if (sliceHeightPx < 20) {
          break;
        }
        let isBlank = true;
        try {
          const imgDataArr = sliceCtx.getImageData(0, 0, sliceCanvas.width, sliceCanvas.height).data;
          const sampleStep = 4 * 20;
          for (let i = 0; i < imgDataArr.length; i += sampleStep) {
            const r = imgDataArr[i];
            const g = imgDataArr[i + 1];
            const b = imgDataArr[i + 2];
            if (!(r >= 250 && g >= 250 && b >= 250)) {
              isBlank = false;
              break;
            }
          }
        } catch (e) {
          isBlank = false;
        }

        if (isBlank) {
          break;
        }

        const imgData = sliceCanvas.toDataURL('image/png');
        const imgHeightInPdf = sliceHeightPx * mmPerPixel;

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', marginMm, marginMm, contentWidthMm, imgHeightInPdf);

        yOffset += sliceHeightPx;
        pageIndex += 1;
      }

      pdf.save(`form-details-${selectedItem?.store_id || 'report'}.pdf`);
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("PDF generation failed. Please try again.");
    }
  };

  if (!selectedItem) {
    return (
      <Sidebar>
        <div className="p-4">
          <p>No data available. Please go back and select a report.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded"
          >
            Go Back
          </button>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="bg-white p-8 rounded-lg w-full shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold border-b pb-2">Form Details</h3>
          <button
            onClick={handleDownloadPDF}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
          >
            Download PDF
          </button>
        </div>


        {/* On-screen view layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {Object.entries(selectedItem).map(([key, value]) => {
            const keyLower = key ? String(key).toLowerCase() : '';
            const isDobFieldTop = keyLower.includes('dob') || keyLower.includes('dateofbirth') || keyLower.includes('birth');
            if (excludedFields.includes(key) || isDobFieldTop) return null;

            if (typeof value === 'object' && value !== null) {
              return (
                <div key={key} className="md:col-span-2">
                  <h4 className="text-lg font-semibold mb-2">{formatLabel(key)}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded">
                    {Object.entries(value).map(([subKey, subVal]) => {
                      const subKeyLower = subKey ? String(subKey).toLowerCase() : '';
                      const isSubDob = subKeyLower.includes('dob') || subKeyLower.includes('dateofbirth') || subKeyLower.includes('birth');
                      if (isSubDob) return null;

                      return (
                        <div key={subKey}>
                          <label className="text-sm font-medium text-gray-600">
                            {formatLabel(subKey)}
                          </label>
                          <input
                            type="text"
                            value={displayValue(subVal)}
                            readOnly
                            className="mt-1 px-3 py-2 border border-gray-300 rounded bg-gray-100 w-full"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            } else {
              return (
                <div key={key}>
                  <label className="text-sm font-medium text-gray-600">
                    {formatLabel(key)}
                  </label>
                  <input
                    type="text"
                    value={displayValue(value)}
                    readOnly
                    className="mt-1 px-3 py-2 border border-gray-300 rounded bg-gray-100 w-full"
                  />
                </div>
              );
            }
          })}
        </div>
        <div
          ref={contentRef}
          style={{
            position: 'absolute',
            top: '-9999px',
            left: '-9999px',
            backgroundColor: '#fff',
            color: '#000',
            padding: '30px',
            width: '794px',
            fontFamily: 'Arial',
            lineHeight: '1.6',
            fontSize: '14px',
          }}
        >
          <h2 style={{ textAlign: 'center', backgroundColor: '#E57320', color: '#fff', padding: '15px', marginBottom: '30px', fontSize: '20px' }} >Mitra Report Form</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', pageBreakInside: 'auto', marginBottom: '20px' }}>
            <tbody>
              {Object.entries(selectedItem).map(([key, value]) => {
                const keyLower = key ? String(key).toLowerCase() : '';
                const isDobField = keyLower.includes('dob') || keyLower.includes('dateofbirth') || keyLower.includes('birth');
                if (excludedFields.includes(key) || isDobField) return null;

                if (typeof value === 'object' && value !== null) {
                  return (
                    <React.Fragment key={key}>
                      <tr style={{ pageBreakInside: 'avoid', pageBreakAfter: 'auto' }}>
                        <td
                          colSpan={2}
                          style={{
                            fontWeight: 'bold',
                            padding: '15px 0 10px 0',
                            fontSize: '16px',
                            borderTop: '2px solid #ccc',
                            marginTop: '10px'
                          }}
                        >
                          {formatLabel(key)}
                        </td>
                      </tr>
                      {Object.entries(value).map(([subKey, subVal]) => {
                        const subKeyLower = subKey ? String(subKey).toLowerCase() : '';
                        const isSubDob = subKeyLower.includes('dob') || subKeyLower.includes('dateofbirth') || subKeyLower.includes('birth');
                        if (isSubDob) return null;

                        return (
                          <tr key={subKey} style={{
                            pageBreakInside: 'avoid',
                            pageBreakAfter: 'auto',
                            borderBottom: '1px solid #f0f0f0'
                          }}>
                            <td style={{
                              width: '40%',
                              padding: '10px 6px',
                              fontWeight: 'bold',
                              verticalAlign: 'top'
                            }}>
                              {formatLabel(subKey)}
                            </td>
                            <td style={{
                              padding: '10px 6px',
                              wordWrap: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {displayValue(subVal)}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                } else {
                  const isLongTextField = key.toLowerCase().includes('description') ||
                    key.toLowerCase().includes('occurred') ||
                    key.toLowerCase().includes('details');

                  return (
                    <tr key={key} style={{
                      pageBreakInside: isLongTextField ? 'avoid' : 'avoid',
                      pageBreakAfter: 'auto',
                      pageBreakBefore: isLongTextField && displayValue(value).length > 100 ? 'auto' : 'auto'
                    }}>
                      <td style={{
                        width: '40%',
                        padding: '8px 6px',
                        fontWeight: 'bold',
                        verticalAlign: 'top'
                      }}>
                        {formatLabel(key)}
                      </td>
                      <td style={{
                        padding: '8px 6px',
                        wordWrap: 'break-word',
                        whiteSpace: 'pre-wrap',
                        maxWidth: '60%'
                      }}>
                        {displayValue(value)}
                      </td>
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        </div>

        <div className="text-right mt-8">
          <button
            onClick={() => navigate(-1)}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded"
          >
            Back
          </button>
        </div>
      </div>
    </Sidebar>
  );
};

export default ReportViewPage;
